import { JSX, useState } from "react";
import { ActiveLabel, Bar, BarChart, BarShapeProps, BaseTickContentProps, CartesianGrid, Label, LabelList, LabelProps, Rectangle, ReferenceLine, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { MarketPenetrationData, MarketPenetrationTypeEnum } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { colors, percentOfMarketColors, widgetTooltipCursor } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { printNumberAsString } from "@app/shared/waffles";
import { useHoverBoundary } from "@app/shared/hooks/useHoverBoundary";
import { ContractDisplayInformation } from "@app/overviews/network-benchmarking-dashboard/filters/shared/ContractDisplayInformation";

interface MarketPenetrationProps {
  customYAxisTick?: (props: BaseTickContentProps) => JSX.Element;
  data: MarketPenetrationData[]
  getContractDisplayInformation: (contractId: string) => ContractDisplayInformation;
  largetInNetworkOnlyValue: number;
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    volumeLabel?: ClaimsActivityVolumeLabelEnum,
  ) => void;
  selectedNetwork: string;
}

const MarketPenetration = (props: MarketPenetrationProps): JSX.Element => {
  const [bar, setBar] = useState<MarketPenetrationTypeEnum>(null);
  const { isHovered: isChartHovered, ref: chartRef } = useHoverBoundary<HTMLDivElement>();

  const onNetworkCompositionClick = (activeLabel: ActiveLabel): void => {
    // only allow to drill into the dashboard details view when
    // clicking in a bar section and the ability to drill into the dashboard is enabled
    if (!!activeLabel && !!bar) {
      const contractData: MarketPenetrationData = props.data.find(x => x.contractId === activeLabel);

      let networkDrillIn: BenchmarkingDashboardDetailsTypeEnum;
      let count;

      switch (bar) {
        case MarketPenetrationTypeEnum.MarketOnly:
          networkDrillIn = BenchmarkingDashboardDetailsTypeEnum.MarketOutOfNetworkDrillin;
          count = contractData.universeProviderCount;
          break;
        case MarketPenetrationTypeEnum.Overlap:
          networkDrillIn = BenchmarkingDashboardDetailsTypeEnum.NetworkInMarketDrillin;
          count = contractData.networkMarketOverlapCount;
          break;
        case MarketPenetrationTypeEnum.InNetworkOnly:
          networkDrillIn = BenchmarkingDashboardDetailsTypeEnum.NetworkOutOfMarketDrillin;
          count = contractData.inNetworkOnlyCount;
          break;
        default:
          break;
      }

      if (!!contractData && contractData.universeProviderCount) {
        props.onDashboardDetailsViewClick(
          networkDrillIn,
          count,
          contractData.versionId,
        );
      }
    }
  }

  const customTooltip = (customTooltipProps: TooltipContentProps<ValueType, string | number>): JSX.Element => {
    if (customTooltipProps.payload?.length) {
      const networkCompositionData: MarketPenetrationData = props.data.find(x => x.contractId === customTooltipProps.label);
      const contractDisplayInformation: ContractDisplayInformation = props.getContractDisplayInformation(customTooltipProps.payload[0].payload.contractId);

      if (!!contractDisplayInformation) {
        return (
          <div className="qes-dashboard__custom-tooltip">
            {!!contractDisplayInformation.parentOrganizationName && (
              <p className="qes-dashboard__tooltip-title mb-0">{contractDisplayInformation.parentOrganizationName}</p>
            )}
            <p className="qes-dashboard__tooltip-title mb-0">{contractDisplayInformation.networkName}</p>
            <i className="qes-dashboard__color-rect" style={{ backgroundColor: `${percentOfMarketColors.lightBlue}` }} /> Market Only: {printNumberAsString(networkCompositionData.universeProviderCount, "---")} ({networkCompositionData.universeProviderPercentage}%)<br />
            <i className="qes-dashboard__color-rect" style={{ backgroundColor: `${percentOfMarketColors.purple}` }} /> Overlap: {printNumberAsString(networkCompositionData.networkMarketOverlapCount, "---")} ({networkCompositionData.networkMarketOverlapPercentage}%)<br />
            <i className="qes-dashboard__color-rect" style={{ backgroundColor: `${customTooltipProps.label === props.selectedNetwork ? colors.green : colors.blue}` }} /> In-Network Only: {printNumberAsString(networkCompositionData.inNetworkOnlyCount, "---")} (+{networkCompositionData.inNetworkOnlyPercentage}%)<br />
          </div>
        );
      }
    }
  }

  const formatPercentageLabel = (percentage: number, marketPenetrationType: MarketPenetrationTypeEnum): string => {
    let minimumDisplayPercentage;

    // in-network only values have an extra little "+"
    if (props.largetInNetworkOnlyValue < 20) {
      minimumDisplayPercentage = marketPenetrationType === MarketPenetrationTypeEnum.InNetworkOnly ? 10 : 8;
    } else if (props.largetInNetworkOnlyValue < 40) {
      minimumDisplayPercentage = marketPenetrationType === MarketPenetrationTypeEnum.InNetworkOnly ? 13 : 10;
    } else if (props.largetInNetworkOnlyValue < 60) {
      minimumDisplayPercentage = marketPenetrationType === MarketPenetrationTypeEnum.InNetworkOnly ? 16 : 12;
    } else if (props.largetInNetworkOnlyValue < 80) {
      minimumDisplayPercentage = marketPenetrationType === MarketPenetrationTypeEnum.InNetworkOnly ? 19 : 14;
    } else {
      minimumDisplayPercentage = marketPenetrationType === MarketPenetrationTypeEnum.InNetworkOnly ? 21 : 16;
    }

    if (percentage >= minimumDisplayPercentage) {
      return (marketPenetrationType === MarketPenetrationTypeEnum.InNetworkOnly ? "+" : "") + printNumberAsString(percentage, "0") + "%";
    } else {
      return;
    }
  }

  const getCellColor = (marketPenetrationType: MarketPenetrationTypeEnum, contractId: string): string => {
    switch (marketPenetrationType) {
      case MarketPenetrationTypeEnum.MarketOnly:
        return percentOfMarketColors.lightBlue;
      case MarketPenetrationTypeEnum.Overlap:
        return percentOfMarketColors.purple;
      case MarketPenetrationTypeEnum.InNetworkOnly:
        return contractId === props.selectedNetwork ? colors.green : colors.blue;
      default:
    }
  }

  const getInNetworkOnlyLabel = ({ viewBox }: LabelProps) => {
    const { x } = (viewBox ?? {}) as { x?: number };
    let offset = 0;

    if (props.largetInNetworkOnlyValue < 20) {
      offset = 10;
    } else if (props.largetInNetworkOnlyValue < 40) {
      offset = 30;
    } else if (props.largetInNetworkOnlyValue < 60) {
      offset = 50;
    } else if (props.largetInNetworkOnlyValue < 80) {
      offset = 70;
    } else {
      offset = 90;
    }

    return (
      <text x={x + offset} y={270}>
        <tspan x={x + offset}>In-Network</tspan>
        <tspan x={x + offset + 20} dy="1.2em">Only</tspan>
      </text>
    );

  }

  const getMarketPosition = (): number => {
    if (props.largetInNetworkOnlyValue < 20) {
      return -45;
    } else if (props.largetInNetworkOnlyValue < 40) {
      return -70;
    } else if (props.largetInNetworkOnlyValue < 60) {
      return -95;
    } else if (props.largetInNetworkOnlyValue < 80) {
      return -115;
    } else {
      return -130;
    }
  }

  const onBarMouseOver = (marketPenetrationType: MarketPenetrationTypeEnum): void => {
    if (bar !== marketPenetrationType) {
      setBar(marketPenetrationType);
    }
  }

  const tickFormatter = (): string => {
    // ticks are needed to correctly display the vertical cartesian grid,
    // despite the fact that we don't actually want to display them
    return "";
  }

  return (
    <div
      ref={chartRef}
      style={{ height: "300px", width: "100%" }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          barCategoryGap="20%"
          data={props.data}
          layout="vertical"
          margin={{
            bottom: 15,
            left: 0,
            right: 0,
            top: 20,
          }}
          onClick={(e) => onNetworkCompositionClick(e.activeLabel)}
        >
          <CartesianGrid strokeDasharray="1 0" horizontal={false} />
          <XAxis type="number"
            ticks={[0, 25, 50, 75, 100, 125, 150, 175, 200].filter(x => x < (props.largetInNetworkOnlyValue + 100))}
            tickFormatter={tickFormatter}
            axisLine={false} tickLine={false}>
            <Label dx={getMarketPosition()} dy={0} value="Market" fill="#32323C" offset={0} />
          </XAxis>
          <YAxis dataKey="contractId" type="category" tickLine={false} tick={props.customYAxisTick} />
          <Tooltip content={(customTooltipProps) => customTooltip(customTooltipProps)} cursor={widgetTooltipCursor} active={isChartHovered ? undefined : false} />
          <ReferenceLine
            x={100}
            stroke="black"
            strokeDasharray="3 3"
            label={(
              <Label
                content={getInNetworkOnlyLabel}
              />
            )}
          />
          {Object.values(MarketPenetrationTypeEnum).map((marketPenetrationType, marketPenetrationTypeIndex) => (
            <Bar
              onMouseOver={() => onBarMouseOver(marketPenetrationType)}
              dataKey={`${marketPenetrationType}DisplayPercentage`}
              key={marketPenetrationTypeIndex}
              stackId="a"
              isAnimationActive={false}
              shape={(shapeProps: BarShapeProps) => (
                <Rectangle
                  {...shapeProps}
                  fill={getCellColor(marketPenetrationType, (shapeProps.payload as MarketPenetrationData).contractId)}
                  cursor="pointer"
                />
              )}
            >
              <LabelList
                dataKey={`${marketPenetrationType}Percentage`}
                formatter={(percentage: number) => formatPercentageLabel(percentage, marketPenetrationType)}
                position="inside"
                fill="black"
                style={{ fontSize: "12px" }}
              />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default MarketPenetration;
